/**
 * Abuse controls — the block-lists, and what one caller is currently up against.
 *
 * ⛔ **The two refusals are reported separately, and that is the whole point of this screen.** A
 * rate window clears itself in minutes; a block does not clear at all until somebody lifts it.
 * An agent who cannot tell them apart resets the counters, tells the devotee to try again, and
 * sends them straight back into the same wall — so `blocked` gets its own line, in its own words,
 * above the windows rather than mixed in among them.
 *
 * **A phone prefix and an IP range share one list**, because they are the same operator act: this
 * source may not reach us, because, until. The server discriminates on `type`; splitting them into
 * two tabs would be two places to look when somebody asks why a caller is refused.
 *
 * **Expired blocks stay listed and are marked spent.** They are the history somebody wants when
 * the same prefix turns up again — a lifted block that was right the first time is the strongest
 * argument for the second one — and there is no cleanup job removing them on purpose.
 *
 * **A reason is required to block and required to lift.** That is the compensating control for
 * this living on `support` rather than `superadmin`: the people who meet a wrongly-blocked devotee
 * are the support desk, and an unblock that needs a founder waits until Monday.
 *
 * The screen writes no rule of its own. Whether a value is a valid range, whether a duplicate is
 * refused, and which scope may do any of it are the server's, and its refusals are shown verbatim.
 */
import { useState } from 'react';
import { Button, StatusChip } from '../components/primitives';
import { DataTable } from '../components/data';
import { Input, Select } from '../components/forms';
import { EmptyState, ErrorState, LoadingState } from '../components/feedback';
import { ConfirmAction, Section, StatTile } from '../components/kit';
import { errorMessage } from '../lib/api/client';
import { useConsole } from '../app/console';
import {
  BLOCK_TYPES,
  PRINCIPAL_TYPES,
  useAddBlock,
  useBlocklist,
  usePrincipal,
  useRemoveBlock,
  useResetPrincipal,
  type BlockRow,
  type BlockType,
  type PrincipalType,
} from '../lib/api/hooks/abuse';
import { formatDateTime } from '../lib/datetime';

const PAGE_SIZE = 25;

/** What an operator calls each kind, on a screen where both are in one table. */
const TYPE_LABEL: Record<BlockType, string> = {
  phone_prefix: 'Phone prefix',
  ip_range: 'IP range',
};

/**
 * The operator's word for a block kind.
 *
 * ⛔ Falls back to the raw value rather than rendering nothing. `BlockRow.type` is a `string` on
 * the wire, not a closed enum, so a kind this build has no word for is representable — and showing
 * `undefined` in the Kind column would read as a broken row rather than a newer server.
 */
export function typeLabel(type: string): string {
  return TYPE_LABEL[type as BlockType] ?? type;
}

/** What a value of each kind looks like — the placeholder follows the picker. */
export const PRINCIPAL_EXAMPLE: Record<PrincipalType, string> = {
  phone: '+919876543210',
  ip: '203.0.113.7',
  user: '42',
};

/**
 * Whether a block has run out.
 *
 * ⛔ Computed for the LABEL only. The server filters expired rows where it reads the list, so an
 * expired row already refuses nobody; this is the screen saying so, not the screen deciding it.
 */
export function isSpent(row: BlockRow, now: Date = new Date()): boolean {
  return row.expires_at !== null && row.expires_at !== undefined
    ? new Date(row.expires_at).getTime() <= now.getTime()
    : false;
}

/** `until somebody removes it` is a deliberate choice, not an oversight — so it is said. */
export function untilLabel(row: BlockRow): string {
  if (!row.expires_at) return 'Until lifted';
  return formatDateTime(row.expires_at);
}

export function Abuse() {
  const { toast } = useConsole();

  const [filter, setFilter] = useState<'' | BlockType>('');
  const [page, setPage] = useState(1);
  const blocks = useBlocklist({
    page,
    page_size: PAGE_SIZE,
    ...(filter === '' ? {} : { type: filter }),
  });

  const [newType, setNewType] = useState<BlockType>('phone_prefix');
  const [newValue, setNewValue] = useState('');
  const [newExpiry, setNewExpiry] = useState('');
  const [adding, setAdding] = useState(false);
  const add = useAddBlock();

  const [lifting, setLifting] = useState<BlockRow | null>(null);
  const remove = useRemoveBlock();

  const [lookupType, setLookupType] = useState<PrincipalType>('phone');
  const [lookupDraft, setLookupDraft] = useState('');
  const [lookup, setLookup] = useState('');
  const principal = usePrincipal(lookupType, lookup);
  const reset = useResetPrincipal();
  const [resetting, setResetting] = useState(false);

  const rows = blocks.data?.items ?? [];
  const total = blocks.data?.total ?? 0;
  const pages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <>
      <p style={{ margin: 0, fontSize: 'var(--fs-admin-cell)', color: 'var(--c-ink2)' }}>
        Two different refusals live here. A rate window clears itself; a block does not. Look a
        caller up before you clear anything — resetting the counters on somebody who is blocked
        sends them back into the same wall.
      </p>

      {/* ── Look one caller up ─────────────────────────────────────────── */}

      <Section
        title="What is this caller up against?"
        note="The commonest reason to open this screen: somebody cannot get their code"
      >
        <div style={{ display: 'flex', gap: 'var(--sp-md)', alignItems: 'flex-end' }}>
          <Select
            // ⛔ Named for what it picks. This screen has two kind-pickers — one for the caller
            // being looked up, one for the source being blocked — and two controls announced as
            // "Kind" are indistinguishable to a screen reader and ambiguous to everyone else.
            label="Look up by"
            value={lookupType}
            onChange={(next) => setLookupType(next as PrincipalType)}
            options={[...PRINCIPAL_TYPES]}
          />
          <Input
            label="Value"
            value={lookupDraft}
            onChange={setLookupDraft}
            placeholder={PRINCIPAL_EXAMPLE[lookupType]}
            style={{ flex: 1 }}
          />
          <Button
            variant="primary"
            disabled={lookupDraft.trim().length === 0}
            onClick={() => setLookup(lookupDraft.trim())}
          >
            Look up
          </Button>
        </div>

        {principal.isPending && lookup !== '' && <LoadingState message="Reading the caller…" />}
        {principal.isError && (
          <ErrorState title="Could not look that caller up" cause={errorMessage(principal.error)} />
        )}
        {principal.isSuccess && (
          <div style={{ display: 'grid', gap: 'var(--sp-md)' }}>
            {/* ⛔ Its own tile, above the windows. A block is not a bigger counter — it is a
                different refusal, and it does not clear when the counters do. */}
            <StatTile
              label="Blocked"
              value={principal.data.blocked ? 'Yes' : 'No'}
              note={
                principal.data.blocked
                  ? 'A block-list entry covers this caller. Clearing the windows will not free them — lift the block.'
                  : 'No block-list entry covers this caller.'
              }
              tone={principal.data.blocked ? 'error' : 'default'}
            />

            {principal.data.windows.length === 0 ? (
              <EmptyState
                icon="activity"
                title="No live rate windows"
                body="Nothing is counting against this caller right now."
                inline
              />
            ) : (
              <DataTable
                density="compact"
                columns={[
                  { key: 'scope', label: 'Window' },
                  { key: 'count', label: 'Requests', align: 'right', mono: true },
                  { key: 'resets', label: 'Clears in', align: 'right', mono: true },
                ]}
                rows={principal.data.windows.map((w) => ({
                  id: w.scope,
                  scope: w.scope,
                  count: w.count,
                  resets: `${w.resets_in_s}s`,
                }))}
              />
            )}

            <div>
              <Button
                variant="secondary"
                size="sm"
                disabled={principal.data.windows.length === 0}
                onClick={() => setResetting(true)}
              >
                Give this caller their quota back
              </Button>
            </div>
          </div>
        )}
      </Section>

      {/* ── The block-list ─────────────────────────────────────────────── */}

      <Section
        title="Block-list"
        note={`${total} ${total === 1 ? 'entry' : 'entries'} · page ${page} of ${pages} · expired entries stay listed as history`}
        actions={
          <Button variant="secondary" size="sm" onClick={() => setAdding(true)}>
            Block a source
          </Button>
        }
        pad={false}
      >
        <div style={{ padding: 'var(--sp-md) var(--sp-lg)', maxWidth: 320 }}>
          <Select
            label="Show"
            value={filter}
            onChange={(next) => {
              setPage(1);
              setFilter(next as '' | BlockType);
            }}
            options={['', ...BLOCK_TYPES]}
            placeholder="Both kinds"
          />
        </div>

        {blocks.isPending && <LoadingState message="Reading the block-list…" />}
        {blocks.isError && (
          <ErrorState title="Could not read the block-list" cause={errorMessage(blocks.error)} />
        )}
        {blocks.isSuccess && rows.length === 0 && (
          <EmptyState
            icon="shield"
            title="Nothing is blocked"
            body="No prefix or range is refused. That is the normal state."
            inline
            style={{ padding: '0 var(--sp-lg) var(--sp-lg)' }}
          />
        )}
        {rows.length > 0 && (
          <DataTable
            density="compact"
            columns={[
              { key: 'kind', label: 'Kind' },
              { key: 'value', label: 'Source', mono: true },
              { key: 'reason', label: 'Why' },
              { key: 'until', label: 'Until' },
              { key: 'state', label: 'State' },
              { key: 'act', label: '' },
            ]}
            rows={rows.map((row) => ({
              id: row.id,
              kind: typeLabel(row.type),
              value: row.value,
              reason: row.reason,
              until: untilLabel(row),
              state: isSpent(row) ? (
                <StatusChip tone="neutral" label="expired" />
              ) : (
                <StatusChip tone="error" label="blocking" />
              ),
              act: (
                <Button variant="secondary" size="sm" onClick={() => setLifting(row)}>
                  Lift
                </Button>
              ),
            }))}
          />
        )}
      </Section>

      {rows.length > 0 && pages > 1 && (
        <div style={{ display: 'flex', gap: 'var(--sp-sm)', justifyContent: 'flex-end' }}>
          <Button variant="secondary" size="sm" disabled={page === 1} onClick={() => setPage((p) => p - 1)}>
            Previous
          </Button>
          <Button variant="secondary" size="sm" disabled={page >= pages} onClick={() => setPage((p) => p + 1)}>
            Next
          </Button>
        </div>
      )}

      <ConfirmAction
        open={adding}
        title="Block a source"
        phrase="BLOCK"
        tone="error"
        reason="Why this source is being blocked"
        confirmLabel="Block it"
        consequences={[
          'Every caller under this prefix or inside this range is refused.',
          'They are told they are going too fast — never that they are blocked, which would tell an attacker to move.',
          'It takes effect immediately, and it stays until somebody lifts it or it expires.',
        ]}
        onCancel={() => setAdding(false)}
        onConfirm={(reason) => {
          add.mutate(
            {
              type: newType,
              value: newValue.trim(),
              reason,
              ...(newExpiry === '' ? {} : { expires_at: new Date(newExpiry).toISOString() }),
            },
            {
              onSuccess: () => {
                setAdding(false);
                setNewValue('');
                setNewExpiry('');
                toast({ message: 'Blocked. It is refusing callers now.' });
              },
              // The server owns every rule here — a malformed range, a duplicate. Its refusal is
              // shown as it wrote it; guessing at one would eventually be wrong and confident.
              onError: (error) => toast({ tone: 'error', message: errorMessage(error) }),
            },
          );
        }}
      >
        <div style={{ display: 'grid', gap: 'var(--sp-md)' }}>
          <Select
            label="Source kind"
            value={newType}
            onChange={(next) => setNewType(next as BlockType)}
            options={[...BLOCK_TYPES]}
          />
          <Input
            label="Source"
            value={newValue}
            onChange={setNewValue}
            placeholder={newType === 'phone_prefix' ? '+919876' : '203.0.113.0/24'}
            helper={
              newType === 'phone_prefix'
                ? 'A prefix, not a number — pumping rotates inside a block the attacker owns.'
                : 'A CIDR range, v4 or v6. A bare address means that one machine.'
            }
          />
          <Input
            label="Expires (optional)"
            value={newExpiry}
            onChange={setNewExpiry}
            placeholder="2026-09-30T00:00"
            helper="Leave empty to block until somebody lifts it. That is a choice, so make it deliberately."
          />
        </div>
      </ConfirmAction>

      {/* ⛔ Rendered only when there IS something to lift, rather than mounted always with a
          `if (!lifting) return` inside. That guard was unreachable — the dialog cannot open
          without a row — and an unreachable guard is a claim nobody can check. */}
      {lifting !== null && (
        <ConfirmAction
          open
          title={`Lift the block on ${lifting.value}`}
          phrase="LIFT"
          reason="Why this source is clean"
          confirmLabel="Lift it"
          consequences={[
            'This source can reach us again immediately.',
            'The entry is removed. The audit log keeps who lifted it and why.',
          ]}
          onCancel={() => setLifting(null)}
          onConfirm={(reason) => {
            remove.mutate(
              { id: lifting.id, type: lifting.type as BlockType, reason },
              {
                onSuccess: () => {
                  setLifting(null);
                  toast({ message: 'Lifted.' });
                },
                onError: (error) => toast({ tone: 'error', message: errorMessage(error) }),
              },
            );
          }}
        />
      )}

      <ConfirmAction
        open={resetting}
        title="Give this caller their quota back"
        phrase="RESET"
        reason="Which ticket this is for"
        confirmLabel="Clear the windows"
        consequences={[
          'Every live rate window for this caller is cleared.',
          'A block is NOT lifted by this. If they are blocked, they will still be refused.',
        ]}
        onCancel={() => setResetting(false)}
        onConfirm={(reason) => {
          reset.mutate(
            { type: lookupType, value: lookup, reason },
            {
              onSuccess: (result) => {
                setResetting(false);
                // ⛔ The server's list, not ours. It returns what it CLEARED, and an agent told
                // "done" when there was nothing there would close a ticket about somebody who is
                // still locked out for a reason this never touched.
                toast({
                  message:
                    result.cleared_scopes.length === 0
                      ? 'Nothing was counting against this caller — nothing was cleared.'
                      : `Cleared: ${result.cleared_scopes.join(', ')}.`,
                });
              },
              onError: (error) => toast({ tone: 'error', message: errorMessage(error) }),
            },
          );
        }}
      />
    </>
  );
}
